import { faLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { InputRule } from "prosemirror-inputrules";
import { Fragment, Mark, Node, Slice } from "prosemirror-model";
import { EditorState, Plugin, Transaction } from "prosemirror-state";
import { insertPoint } from "prosemirror-transform";
import { EditorView } from "prosemirror-view";
import { v4 } from "uuid";

import { toggleMark } from "~/lib/prose/commands";
import { isZoom, linkRegexNewline } from "~/lib/utils";

import { MarkExtension } from "../extension";
import PlaceholderMark from "./placeholder";

const linkRegex = new RegExp("(?:https?://)(?:[\\w\\.\\-]+)(?::\\d{2,5})?(?:[/?#]\\S*)?", "ig");

const linkRegexInputRule = linkRegexNewline;

function getPastedPlainText(slice: Slice): string | null {
	let pastedPlainText = false;
	if (slice.content.childCount != 1) {
		return null;
	}
	const firstPastedChild = slice.content.child(0);
	if (firstPastedChild.type.name === "text") {
		pastedPlainText = true;
	} else if (firstPastedChild.childCount === 1 && firstPastedChild.child(0).type.name === "text") {
		pastedPlainText = true;
	}
	if (!pastedPlainText) return null;
	return slice.content.textBetween(0, slice.content.size);
}

function linkify(content: Fragment, parent?: Node): Fragment {
	const fragment: Node[] = [];
	if (parent && !parent.type.allowsMarkType(parent.type.schema.marks["link"])) {
		return content;
	}
	content.forEach(node => {
		if (node.isText && node.text) {
			let pos = 0;
			let match: RegExpMatchArray | null = null;
			while ((match = linkRegex.exec(node.text)) && match.index !== undefined) {
				const start = match.index,
					end = match.index + match[0].length;
				if (start > 0) {
					fragment.push(node.cut(pos, start));
				}
				const text = node.text.slice(start, end);
				fragment.push(
					node
						.cut(start, end)
						.mark(node.type.schema.marks["link"].create({ url: text, entityId: v4() }).addToSet(node.marks)),
				);
				pos = end;
			}
			if (pos < node.text.length) {
				fragment.push(node.cut(pos));
			}
		} else {
			fragment.push(node.copy(linkify(node.content, node)));
		}
	});
	return Fragment.fromArray(fragment);
}

class LinkMark extends MarkExtension {
	allowEditing: boolean;
	constructor(options = { allowEditing: true }) {
		super();
		this.allowEditing = options.allowEditing;
	}
	get name() {
		return "link";
	}

	keys() {
		if (!this.allowEditing) return null;
		return {
			"Mod-k": (
				state: EditorState,
				dispatch: ((tr: Transaction) => void) | undefined,
				view: EditorView | undefined,
			) => {
				if (!view || view.state.selection.empty) {
					return false;
				}
				return LinkMark.insertLink("")(state, dispatch);
			},
		};
	}

	get plugins() {
		return [
			// plugin to auto convert text to links
			new Plugin({
				props: {
					transformPasted(slice) {
						return new Slice(linkify(slice.content), slice.openStart, slice.openEnd);
					},
				},
			}),
			// Plugin to auto make pasted raw link text pills
			new Plugin({
				props: {
					handlePaste(view, _event, slice) {
						if (isZoom) return false;
						const pillNodeType = view.state.schema.nodes["link_pill"];
						if (!pillNodeType) return false;
						const {
							selection: { from, empty, $from },
						} = view.state;
						if (insertPoint(view.state.doc, from, view.state.schema.nodes["text"]) !== from) {
							return false;
						}
						if ($from.marks().find(m => m.type.name === "link")) return false;
						if (!empty) return;
						const pastedPlainText = getPastedPlainText(slice);
						if (!pastedPlainText) return false;
						const urlMatch = pastedPlainText.match(linkRegex);
						const url = urlMatch?.[0];
						if (!url) return false;
						const newPill = pillNodeType.createAndFill({ entityId: v4(), url }, view.state.schema.text(url));
						if (!newPill) return false;
						view.dispatch(view.state.tr.replaceRangeWith(from, from, newPill));
						return true;
					},
				},
			}),
			// Plugin to create hyperlinks by selecting text and pasting a link, like slack.
			// Should be last in this plugins list.
			new Plugin({
				props: {
					handlePaste({ state, dispatch }, _, slice) {
						// Make sure we're pasting inline
						const pasteTarget = state.selection.content().content;
						if (pasteTarget.childCount !== 1) return false;
						let canAddMarkToSelectedRange = true;
						pasteTarget.descendants(n => {
							if (n.type.name !== "text" && !n.type.allowsMarkType(state.schema.marks["link"])) {
								// prosemirror allows a mark around a node of a type that doesn't allow that mark.
								// But we interpret that differently to mean not allowed in or around.
								canAddMarkToSelectedRange = false;
								return false;
							}
							if (n.marks.find(m => m.type.name === "link")) {
								canAddMarkToSelectedRange = false;
								return false;
							}
							return true;
						});

						if (!canAddMarkToSelectedRange) return false;

						// Do not allow a link being applied over a placeholder
						if (PlaceholderMark.selectionContains(state.selection)) return false;

						// Check that we're pasting over a selection
						if (state.selection.from === state.selection.to) return false;

						/* The rest is to check if the clipboard has a single, linkified block */

						// Check that we have a single block
						if (slice.content.childCount !== 1) return false;

						// Check that we have one child
						const child = slice.content.firstChild;
						if (!child || (!child.inlineContent && !child.text)) return false;

						// We don't have nested blocks (normally) this is really a sanity check
						if (child.content.childCount !== 1 && !child.text) return false;

						// Check if the text has the link mark, and the get the link if so
						let url = child.textContent.match(linkRegex)?.[0] || "";
						child.content.firstChild?.marks.find(mark => {
							if (mark.type.name === "link") {
								url = mark.attrs.url;
								return true;
							}
							return false;
						});
						if (!url) return false;

						LinkMark.insertLink(url)(state, dispatch);
						return true;
					},
				},
			}),
		];
	}

	get mark() {
		return {
			parseDOM: [
				{
					tag: "a",
					getAttrs: (dom: HTMLElement) => {
						if (!dom.getAttribute("href")) return false;
						return {
							url: dom.getAttribute("href"),
							entityId: v4(),
						};
					},
				},
			],
			spanning: false,
			inclusive: false,
			attrs: {
				url: { default: "" },
				entityId: {},
			},
			toDOM(mark: Mark) {
				return [
					"a",
					{
						href: mark.attrs.url,
						"data-entity-id": mark.attrs.entityId,
						class: "prose-link",
						target: "_blank",
						rel: "noopener noreferrer",
					},
					0,
				];
			},
		};
	}

	static insertLink =
		(href: string) =>
		(state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
			const { selection } = state;
			const { $from, $to } = selection;
			if (!$from.sameParent($to)) return false;
			// make a addMark command
			return toggleMark(state.schema.marks["link"], { url: href, entityId: v4() })(state, dispatch);
		};

	inputRules() {
		return [
			new InputRule(linkRegexInputRule, function linkify(state, match, start, end) {
				const href = match[1];
				let onlyText = true;
				state.doc.slice(start, end).content.descendants(n => {
					if (n.type.name !== "text") {
						onlyText = false;
						return false;
					}
					return true;
				});
				if (!onlyText) return null;
				const mark = state.schema.mark("link", { url: href, entityId: v4() });
				/* We used to attempt to embed here, but it resulted in this:
					https://fellowinsights.slack.com/archives/C023CMLMCU8/p1626968940142600 */
				return state.tr.insertText(" ", end, end).addMark(start, end, mark);
			}),
		];
	}

	menuEntry() {
		return {
			icon: faLink,
			title: "Link",
			greyIfDryRunFalse: true,
			run:
				() =>
				(state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
					const {
						selection: { $from, from, to },
					} = state;

					const slice = $from.doc.cut(from, to);
					let hasInlineBlock = false;
					slice.descendants(n => {
						if (hasInlineBlock) {
							return;
						}
						hasInlineBlock = n.type.isInline && n.type.isLeaf === false;
					});
					let hasNonTextLeaf = false;
					slice.descendants(n => {
						if (hasNonTextLeaf) {
							return;
						}
						hasNonTextLeaf = n.isLeaf && !n.isText;
					});
					return LinkMark.insertLink("")(state, dispatch);
				},
			section: "mostUsedAndSocial" as const,
			priority: 2,
		};
	}
}

// WORKAROUND: STORYBOOK (https://github.com/styleguidist/react-docgen-typescript/issues/394)
export default LinkMark;
